import React from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";

import { PrimaryButton } from "../../../../common/native";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { formatPhoneNumberDisplay } from "../../../../../utils/phoneNumber";
import { formatDateTime } from "../utils/formatters";
import useCallSession from "./useCallSession";

const formatProcedureLabel = (value: string): string => {
  const normalizedValue = (value || "").trim();
  if (!normalizedValue) {
    return "";
  }

  return normalizedValue
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const IncomingCallBubble: React.FC = () => {
  const {
    state,
    incomingCall,
    isIncomingActionSubmitting,
    answerIncomingCall,
    declineIncomingCall,
  } = useCallSession();

  const visible =
    state === "incoming_ringing" &&
    Boolean(incomingCall) &&
    typeof document !== "undefined";

  if (!visible || !incomingCall || typeof document === "undefined") {
    return null;
  }

  const { createPortal } = require("react-dom");

  const callerContext = incomingCall.callerContext;
  const procedureSummary = (callerContext.procedures || [])
    .slice(0, 3)
    .map(formatProcedureLabel)
    .filter(Boolean)
    .join(", ");

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Incoming call"
      style={{
        position: "fixed",
        right: ADMIN_TOKENS.spacing.xl,
        bottom: ADMIN_TOKENS.spacing.xl,
        zIndex: 2147483001,
        width: "min(420px, calc(100vw - 40px))",
      }}
    >
      <View
        style={{
          width: "100%",
          borderRadius: ADMIN_TOKENS.radius.xxl,
          borderWidth: ADMIN_TOKENS.borderWidth.sm,
          borderColor: ADMIN_TOKENS.color.borderAccent,
          backgroundColor: ADMIN_TOKENS.color.surface,
          padding: ADMIN_TOKENS.spacing.xl,
          gap: ADMIN_TOKENS.spacing.md,
          ...(ADMIN_TOKENS.shadow.card as object),
        }}
      >
        <View style={{ gap: ADMIN_TOKENS.spacing.xs }}>
          <Text
            style={{
              color: ADMIN_TOKENS.color.textPrimary,
              fontFamily: ADMIN_TOKENS.font.semibold,
              fontSize: ADMIN_TOKENS.fontSize.lg,
              lineHeight: ADMIN_TOKENS.lineHeight.lg,
            }}
          >
            Incoming call
          </Text>
          <Text
            style={{
              color: ADMIN_TOKENS.color.textPrimary,
              fontFamily: ADMIN_TOKENS.font.medium,
              fontSize: ADMIN_TOKENS.fontSize.md,
              lineHeight: ADMIN_TOKENS.lineHeight.md,
            }}
          >
            {formatPhoneNumberDisplay(incomingCall.callerE164, {
              fallback: "Unknown number",
            })}
          </Text>
          {incomingCall.calleeE164 ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.textSecondary,
                fontFamily: ADMIN_TOKENS.font.regular,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              Routed to{" "}
              {formatPhoneNumberDisplay(incomingCall.calleeE164, {
                fallback: incomingCall.calleeE164,
              })}
            </Text>
          ) : null}
        </View>

        <View
          style={{
            borderRadius: ADMIN_TOKENS.radius.lg,
            borderWidth: ADMIN_TOKENS.borderWidth.sm,
            borderColor: ADMIN_TOKENS.color.borderMuted,
            backgroundColor: ADMIN_TOKENS.color.surfaceMuted,
            padding: ADMIN_TOKENS.spacing.md,
            gap: ADMIN_TOKENS.spacing.xs,
          }}
        >
          {callerContext.status === "loading" ? (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: ADMIN_TOKENS.spacing.sm,
              }}
            >
              <ActivityIndicator
                size="small"
                color={ADMIN_TOKENS.color.accent}
              />
              <Text
                style={{
                  color: ADMIN_TOKENS.color.textSecondary,
                  fontFamily: ADMIN_TOKENS.font.regular,
                  fontSize: ADMIN_TOKENS.fontSize.sm,
                }}
              >
                Looking up patient details...
              </Text>
            </View>
          ) : null}

          {callerContext.patientName ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.textPrimary,
                fontFamily: ADMIN_TOKENS.font.medium,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              Patient: {callerContext.patientName}
            </Text>
          ) : callerContext.status !== "loading" ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.textSecondary,
                fontFamily: ADMIN_TOKENS.font.regular,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              No patient match found yet.
            </Text>
          ) : null}

          {procedureSummary ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.textSecondary,
                fontFamily: ADMIN_TOKENS.font.regular,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              Procedures: {procedureSummary}
            </Text>
          ) : null}

          <Text
            style={{
              color: ADMIN_TOKENS.color.textSecondary,
              fontFamily: ADMIN_TOKENS.font.regular,
              fontSize: ADMIN_TOKENS.fontSize.sm,
              lineHeight: ADMIN_TOKENS.lineHeight.sm,
            }}
          >
            Images: {callerContext.imageCount} • Notes:{" "}
            {callerContext.noteCount} • Appointments:{" "}
            {callerContext.appointmentCount}
          </Text>

          {callerContext.nextAppointmentAt ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.textSecondary,
                fontFamily: ADMIN_TOKENS.font.regular,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              Next appointment:{" "}
              {formatDateTime(callerContext.nextAppointmentAt)}
            </Text>
          ) : null}

          {callerContext.errorMessage ? (
            <Text
              style={{
                color: ADMIN_TOKENS.color.errorTextStrong,
                fontFamily: ADMIN_TOKENS.font.medium,
                fontSize: ADMIN_TOKENS.fontSize.sm,
                lineHeight: ADMIN_TOKENS.lineHeight.sm,
              }}
            >
              {callerContext.errorMessage}
            </Text>
          ) : null}
        </View>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: ADMIN_TOKENS.spacing.sm,
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Decline incoming call"
            disabled={isIncomingActionSubmitting}
            onPress={() => declineIncomingCall().catch(() => undefined)}
            style={({ pressed }) => ({
              minHeight: 44,
              flex: 1,
              borderRadius: ADMIN_TOKENS.radius.md,
              borderWidth: ADMIN_TOKENS.borderWidth.sm,
              borderColor: pressed
                ? ADMIN_TOKENS.color.accent
                : ADMIN_TOKENS.color.border,
              backgroundColor: ADMIN_TOKENS.color.surface,
              alignItems: "center",
              justifyContent: "center",
              paddingVertical: ADMIN_TOKENS.spacing.sm,
              paddingHorizontal: ADMIN_TOKENS.spacing.md,
              opacity: isIncomingActionSubmitting
                ? ADMIN_TOKENS.opacity.disabled
                : ADMIN_TOKENS.opacity.full,
            })}
          >
            <Text
              style={{
                color: ADMIN_TOKENS.color.textPrimary,
                fontFamily: ADMIN_TOKENS.font.medium,
                fontSize: ADMIN_TOKENS.fontSize.md,
                lineHeight: ADMIN_TOKENS.lineHeight.md,
              }}
            >
              Decline
            </Text>
          </Pressable>

          <PrimaryButton
            text={isIncomingActionSubmitting ? "Answering..." : "Answer"}
            onPress={() => answerIncomingCall().catch(() => undefined)}
            disabled={isIncomingActionSubmitting}
            style={{
              flex: 1,
              minHeight: 44,
              opacity: isIncomingActionSubmitting
                ? ADMIN_TOKENS.opacity.disabled
                : ADMIN_TOKENS.opacity.full,
            }}
          />
        </View>
      </View>
    </div>,
    document.body,
  );
};

export default IncomingCallBubble;
